import { Grid, makeStyles } from "@material-ui/core";
import { DsIcon, DsPrimaryButton, DsPrimaryLoadingButton, DsSecondaryButton, size } from "@mvp/design-system";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { createCSVApi } from "../../api/createCsvService";
import { isDonateLifeEnabled } from "../../config";
import { useAppStore, useSubscriberStore } from "../../hooks";
import useCreateCsvPayload from '../../hooks/useCreateCsvPayload';
import { useModalContent } from "../../hooks/useModal";
import { useNavigationStore } from "../../hooks/useNavigationStore";
import routes from "../../routes";
import { ModalContent } from "../../store/enums/ModalContent";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.between('md', 'lg')]: {
      marginTop: '1.6rem',
      marginRight: '2.5rem'
    },
    [theme.breakpoints.up('md')]: {
      borderTop: 'solid .125rem #D9D9D9'
    },
    [theme.breakpoints.down("sm")]: {
      height: "4rem",
      position: "fixed",
      left: 0,
      right: 0,
      bottom: 0,
      padding: `0 ${size.m(theme)}`,
      backgroundColor: "white",
      zIndex: 1000,
    },
  },
  backButton: {
    color: "rgba(0,0,0,0.6)",
    [theme.breakpoints.up('md')]: {
      marginLeft: size.xs(theme)
    }, 
  },
  buttonMargin: {
    [theme.breakpoints.up('md')]: {
      marginRight: size.m(theme)
    },  
  },
  iconStyles: {
    marginTop: '.125rem'
  }, 
  buttonContainer: {
    margin: `${size.s(theme)} 0`,
    [theme.breakpoints.down('sm')]: {
      margin: 0,
    }
  }
}));

const NavigationButtons: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { createPayload } = useCreateCsvPayload();
  const { updateModalContent } = useModalContent();
  const { appStore: { planInfo } } = useAppStore();
  const { subscriberDispatch } = useSubscriberStore();
  const { t } = useTranslation();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const locationPath = location.pathname;
  const isFirstPage = locationPath === routes.eligibility.home;
  const isThankYouPage = locationPath === routes.reviewSelection.thankYou;
  const isLegalAgreementsPage = locationPath === routes.reviewSelection.legalAgreements;
  const isDonateLifeInfoPage = locationPath === routes.subscriberInfo.donateLifeInfo;

  const { navigationStore } = useNavigationStore();
  const donateLifeEnabled = isDonateLifeEnabled(planInfo?.planState || "");

  const moveBack = () => {
    history.goBack();
  };

  const moveForward = async () => {
    if (isLegalAgreementsPage) {
      setIsSubmitting(true);
      const result = createPayload();
      createCSVApi(result)
      .then(() => {
        navigationStore.onContinue();
      })
      .catch((err) => {
        updateModalContent(ModalContent.SubmissionError);
      }).finally(() => {
        setIsSubmitting(false);
      });
    } else {
      navigationStore.onContinue();
    }
  };

  if(isThankYouPage) {
    return null;
  }

  const navigateDonateLife = (optIn: boolean) => {
    subscriberDispatch("setDonateLifeOptIn", optIn);
    moveForward();
  }

  return (
    <Grid
      container
      justifyContent={isFirstPage ? "flex-end" : "space-between"}
      alignItems="center"
      className={classes.root}
    >
      {!isFirstPage && (
        <Grid item>
          <DsPrimaryButton
            variant="text"
            text="Back"
            startIcon={<DsIcon icon="angle-left" title="Back icon" className={classes.iconStyles} />}
            onClick={moveBack}
            className={classes.backButton}
          />
        </Grid>
      )}
      <Grid item className={classes.buttonContainer}>
        {donateLifeEnabled && isDonateLifeInfoPage ? (
          <>
            <DsSecondaryButton disabled={navigationStore.isNavDisabled} onClick={() => navigateDonateLife(true)} className={classes.buttonMargin}>
              {t('donateLife.page.yesButton')}
            </DsSecondaryButton>
            <DsSecondaryButton disabled={navigationStore.isNavDisabled} onClick={() => navigateDonateLife(false)} className={classes.buttonMargin}>
              {t('donateLife.page.skipButtonText')}
            </DsSecondaryButton>
          </>
        ) : (
          <DsPrimaryLoadingButton
            disabled={navigationStore.isNavDisabled}
            onClick={moveForward}
            actionInProgress={isSubmitting}
            className={classes.buttonMargin}
          >
            {isLegalAgreementsPage ? "Submit Enrollment" : "Continue"}
          </DsPrimaryLoadingButton>
        )}
      </Grid>
    </Grid>
  );
};

export default NavigationButtons;
